import React from 'react';
import { createPortal } from 'react-dom';

// Human-readable labels for the regex pattern keys the backend emits.
const SENSITIVE_PATTERN_LABELS = {
  absolute_efficacy_claim: 'Absolute efficacy / safety claim',
  regulatory_recommendation: 'Regulatory / prescribing recommendation',
  bare_numeric_no_citation: 'Numeric claim without citation',
};

const prettySensitivePattern = (pattern) =>
  SENSITIVE_PATTERN_LABELS[pattern] || pattern || 'Flagged phrase';

const AgentWarningsModal = ({
  sensitiveFlags = [],
  groundingFlags = [],
  boundsEnforcement = null,
  agentName,
  onClose,
}) => {
  const sensitive = Array.isArray(sensitiveFlags) ? sensitiveFlags : [];
  const grounding = Array.isArray(groundingFlags) ? groundingFlags : [];

  // Only surface the categories that were actually trimmed/truncated.
  const boundsIssues = [];
  if ((boundsEnforcement?.graphs?.dropped ?? 0) > 0) {
    boundsIssues.push({ label: 'Graphs', ...boundsEnforcement.graphs });
  }
  if ((boundsEnforcement?.tables?.dropped ?? 0) > 0) {
    boundsIssues.push({ label: 'Tables', ...boundsEnforcement.tables });
  }
  if ((boundsEnforcement?.images?.dropped ?? 0) > 0) {
    boundsIssues.push({ label: 'Images', ...boundsEnforcement.images });
  }
  const markdownTruncated = boundsEnforcement?.markdown?.truncated === true;

  const boundsIssueCount = boundsIssues.length + (markdownTruncated ? 1 : 0);
  const totalCount = sensitive.length + grounding.length + boundsIssueCount;

  return createPortal(
    <div
      className="ev-modal-overlay d-flex align-items-center justify-content-center p-3"
      style={{ zIndex: 'var(--num-1000000000)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="ev-card-4 d-flex flex-column overflow-hidden vh-80 w-50">
        {/* header */}
        <div className="d-flex align-items-center justify-content-between ev-padding-small border-bottom flex-shrink-0">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <i className="bi bi-exclamation-triangle-fill text-warning"></i>
              <span className="ev-bold-med ev-reg-p-dark">Review Warnings</span>
              <span className="ev-message-card3 ev-btn-xsmall">{totalCount}</span>
            </div>
            {agentName && <span className="ev-message-card1 ev-btn-xsmall">{agentName}</span>}
          </div>
          <button
            type="button"
            className="ev-btn-light-fill ev-btn-xsmall ev-radius-xlarge"
            onClick={onClose}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {/* body */}
        <div className="ev-padding-small scrollable-element flex-grow-1 pt-2">
          {totalCount === 0 && (
            <div className="ev-card-2 text-center ev-padding-reg ev-radius-small">
              <i className="bi bi-check2-circle ev-fs-25 ev-small-p-blue d-block mb-2"></i>
              <p className="ev-small-p-dark mb-0">No warnings for this response.</p>
            </div>
          )}

          {/* Grounding flags */}
          {grounding.length > 0 && (
            <div className="mb-3">
              <div className="ev-bold-med ev-small-p-blue mb-2 d-flex align-items-center gap-2">
                <i className="bi bi-search"></i>
                Grounding issues
                <span className="ev-message-card3 ev-btn-xsmall">{grounding.length}</span>
              </div>

              {grounding.map((f, i) => (
                <div key={`grounding-${i}`} className="ev-card-2 ev-radius-xsmall p-2 mb-2">
                  <p className="ev-small-p-dark mb-1">{f.claim_text}</p>
                  <div className="d-flex flex-wrap align-items-center gap-1 mb-1">
                    {f.doc_id != null && (
                      <span className="ev-message-card1 ev-btn-xsmall ev-radius-xsmall">
                        doc_{f.doc_id}
                      </span>
                    )}
                    {f.grounding_score != null && (
                      <span className="ev-message-card3 ev-btn-xsmall">
                        score {f.grounding_score}
                      </span>
                    )}
                    {f.threshold != null && (
                      <span className="ev-message-card3 ev-btn-xsmall">
                        threshold {f.threshold}
                      </span>
                    )}
                  </div>
                  {f.best_chunk_preview && (
                    <div
                      className="ev-card-8 p-2 ev-radius-xsmall"
                      style={{ borderLeft: '3px solid var(--color1)' }}
                    >
                      <span className="ev-bold-med ev-xsmall-p-dark">Closest source text: </span>
                      <span className="ev-xsmall-p-dark">{f.best_chunk_preview}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Sensitive-output flags */}
          {sensitive.length > 0 && (
            <div className="mb-3">
              <div className="ev-bold-med ev-small-p-blue mb-2 d-flex align-items-center gap-2">
                <i className="bi bi-shield-exclamation"></i>
                Sensitive output
                <span className="ev-message-card3 ev-btn-xsmall">{sensitive.length}</span>
              </div>

              {sensitive.map((f, i) => (
                <div key={`sensitive-${i}`} className="ev-card-2 ev-radius-xsmall p-2 mb-2">
                  <div className="mb-1">
                    <span className="ev-message-card1 ev-btn-xsmall ev-radius-xsmall">
                      {prettySensitivePattern(f.pattern)}
                    </span>
                  </div>
                  <p className="ev-small-p-dark mb-0">{f.matched_text}</p>
                </div>
              ))}
            </div>
          )}

          {/* Content limits (bounds enforcement) — only trimmed/truncated items */}
          {(boundsIssues.length > 0 || markdownTruncated) && (
            <div className="mb-1">
              <div className="ev-bold-med ev-small-p-blue mb-2 d-flex align-items-center gap-2">
                <i className="bi bi-scissors"></i>
                Content limits
                <span className="ev-message-card3 ev-btn-xsmall">{boundsIssueCount}</span>
              </div>

              {boundsIssues.map((b) => (
                <div key={`bounds-${b.label}`} className="ev-card-2 ev-radius-xsmall p-2 mb-2">
                  <div className="d-flex flex-wrap align-items-center gap-1">
                    <span className="ev-message-card1 ev-btn-xsmall ev-radius-xsmall">{b.label}</span>
                    <span className="ev-message-card3 ev-btn-xsmall">original {b.original}</span>
                    <span className="ev-message-card3 ev-btn-xsmall">kept {b.kept}</span>
                    <span className="ev-message-card3 ev-btn-xsmall">dropped {b.dropped}</span>
                  </div>
                </div>
              ))}

              {markdownTruncated && (
                <div className="ev-card-2 ev-radius-xsmall p-2 mb-2">
                  <div className="d-flex flex-wrap align-items-center gap-1">
                    <span className="ev-message-card1 ev-btn-xsmall ev-radius-xsmall">Text</span>
                    <span className="ev-message-card3 ev-btn-xsmall">
                      original {boundsEnforcement.markdown.original_chars} chars
                    </span>
                    <span className="ev-message-card3 ev-btn-xsmall">
                      final {boundsEnforcement.markdown.final_chars} chars
                    </span>
                    <span className="ev-message-card3 ev-btn-xsmall">truncated</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AgentWarningsModal;